import { create } from "zustand";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  type Query,
  type Unsubscribe,
} from "firebase/firestore";
import { db, logException, onSnapshotWithRetry } from "@um/ui";
import type { Item, WithId } from "@um/contracts";

/**
 * Items owned by the account (`Account/{accountId}/Item`). Generation is
 * asynchronous — `onItemCreated` fills in the name, description and image after
 * the doc is written — so consumers must tolerate an item with no image yet.
 */
interface ItemsState {
  items: WithId<Item>[];
  loading: boolean;
  error: string | null;
  subscribeToItems: (accountId: string) => void;
  unsubscribeFromItems: () => void;
  /**
   * Marks the package opened. Stored on the item rather than locally so the
   * moment happens once per account, not once per device.
   */
  openItem: (accountId: string, itemId: string) => Promise<void>;
  /** Records that the wish reveal has been seen, without opening the item. */
  markWishRevealed: (accountId: string, itemId: string, open?: boolean) => Promise<void>;
  /**
   * Brings a parcel's due time forward to now. **Dev only** — real delivery
   * windows run to the better part of an hour.
   */
  deliverNow: (accountId: string, itemId: string) => Promise<void>;
  /**
   * Deletes every item on the account. **Dev only** and destructive — the art is
   * generated content that cannot be recovered. Kept apart from "reset quests",
   * which never touches items, so this stays a deliberate second press.
   */
  removeAllItemsForDev: (accountId: string) => Promise<void>;
}

export const useItemsStore = create<ItemsState>((set) => {
  let unsubscribe: Unsubscribe | null = null;
  let subscribedTo: string | null = null;
  /**
   * How many mounted components want this listener. Items are read from more
   * than one place, and without counting, the first unmount tears the listener
   * out from under the others and freezes their item list.
   */
  let subscribers = 0;

  return {
    items: [],
    loading: false,
    error: null,

    subscribeToItems: (accountId: string) => {
      if (!accountId) {
        set({ items: [], loading: false, error: null });
        return;
      }

      subscribers += 1;
      // Already listening to this account for someone else — just join them.
      if (unsubscribe && subscribedTo === accountId) return;

      unsubscribe?.();
      subscribedTo = accountId;
      set({ items: [], loading: true, error: null });
      // `onSnapshotWithRetry`, not raw `onSnapshot`: a Firestore listener that
      // errors is terminated and never fires again. The first-reward path hits
      // exactly that — auth-link sign-in re-authenticates the streams before the
      // new uid is on the account's ACL — and without the retry the wardrobe
      // stays frozen until a reload. Same reasoning as `useCharactersStore`.
      unsubscribe = onSnapshotWithRetry<Item>(
        query(collection(db, "Account", accountId, "Item")) as Query<Item>,
        (docs) => {
          if (subscribedTo !== accountId) return;
          const items: WithId<Item>[] = docs.map((d) => ({
            ...d,
            id: d.ref.id,
          }));
          set({ items, loading: false, error: null });
        },
        (err) => {
          if (subscribedTo !== accountId) return;
          console.error("Items subscription error", err);
          set({ loading: false, error: err.message });
        }
      );
    },

    unsubscribeFromItems: () => {
      subscribers = Math.max(0, subscribers - 1);
      if (subscribers > 0) return;

      unsubscribe?.();
      unsubscribe = null;
      subscribedTo = null;
      set({ items: [], loading: false, error: null });
    },

    deliverNow: async (accountId: string, itemId: string) => {
      // Guarded here, not just at the call site: this forces a real delivery
      // time onto a real item, so it should not depend on its caller being the
      // dev-only button.
      if (!import.meta.env.DEV || !accountId || !itemId) return;
      try {
        await updateDoc(doc(db, "Account", accountId, "Item", itemId), {
          deliverAt: serverTimestamp(),
          updated: serverTimestamp(),
        });
      } catch (error) {
        logException(error);
        console.error("Error delivering item:", error);
      }
    },


    removeAllItemsForDev: async (accountId: string) => {
      // Guarded here too: a destructive helper should not rely on its callers.
      if (!import.meta.env.DEV || !accountId) return;
      try {
        const snapshot = await getDocs(
          collection(db, "Account", accountId, "Item")
        );
        await Promise.all(snapshot.docs.map((entry) => deleteDoc(entry.ref)));
      } catch (error) {
        logException(error);
        console.error("Error removing items:", error);
      }
    },

    markWishRevealed: async (accountId, itemId, open = false) => {
      if (!accountId || !itemId) throw new Error("Missing account or item.");
      try {
        const ref = doc(db, "Account", accountId, "Item", itemId);
        await runTransaction(db, async (transaction) => {
          const snapshot = await transaction.get(ref);
          if (!snapshot.exists()) throw new Error("This item no longer exists.");
          const item = snapshot.data();
          transaction.update(ref, {
            ...(!item.wishRevealedAt ? { wishRevealedAt: serverTimestamp() } : {}),
            ...(open && !item.openedAt ? { openedAt: serverTimestamp() } : {}),
            updated: serverTimestamp(),
          });
        });
      } catch (error) {
        logException(error);
        throw error;
      }
    },

    openItem: async (accountId, itemId) => {
      if (!accountId || !itemId) throw new Error("Missing account or item.");
      try {
        const ref = doc(db, "Account", accountId, "Item", itemId);
        await runTransaction(db, async (transaction) => {
          const snapshot = await transaction.get(ref);
          if (!snapshot.exists()) throw new Error("This item no longer exists.");
          if (snapshot.data().openedAt) return;
          transaction.update(ref, {
            openedAt: serverTimestamp(),
            updated: serverTimestamp(),
          });
        });
      } catch (error) {
        logException(error);
        throw error;
      }
    },
  };
});

/** Items keyed by id, for `resolveEquippedOverlays`. */
export function itemsById(items: WithId<Item>[]): Map<string, WithId<Item>> {
  return new Map(items.map((item) => [item.id, item]));
}
